import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { searchAMIs, searchFiles } from '../services/api';
import type { AMIResponse, FileSearchRequest, FileSearchResult } from '../types/api';
import Navbar from '../components/Navbar';

const Search = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Image search state
  const [imageSearchTerm, setImageSearchTerm] = useState('');
  const [imageSearchType, setImageSearchType] = useState<'ami' | 'account' | 'name'>('ami');
  const [imageResults, setImageResults] = useState<AMIResponse[]>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageSearchSubmitted, setImageSearchSubmitted] = useState(false);
  const [sortField, setSortField] = useState<keyof AMIResponse>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // File search state
  const [fileSearchTerm, setFileSearchTerm] = useState('');
  const [fileSearchMode, setFileSearchMode] = useState<'hash' | 'file'>('file');
  const [fileSearchType, setFileSearchType] = useState<'filename' | 'filepath'>('filename');
  const [fileMatchType, setFileMatchType] = useState<'exact' | 'contains'>('contains');
  const [fileResults, setFileResults] = useState<FileSearchResult[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileSearchSubmitted, setFileSearchSubmitted] = useState(false);
  const [fileSortField, setFileSortField] = useState<keyof FileSearchResult>('amiId');
  const [fileSortDirection, setFileSortDirection] = useState<'asc' | 'desc'>('asc');

  // On mount, check for URL params and trigger search if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Check for image search params
    const nameParam = params.get('name');
    if (nameParam && nameParam !== imageSearchTerm) {
      setImageSearchTerm(nameParam);
      setImageSearchType('name');
      setActiveTab(0); // Switch to image search tab
      // Trigger search
      (async () => {
        setImageLoading(true);
        setImageError(null);
        setImageSearchSubmitted(true);
        try {
          const response = await searchAMIs(nameParam);
          if (response.error) {
            setImageError(response.error);
            setImageResults([]);
          } else {
            setImageResults(Array.isArray(response.data) ? response.data : []);
          }
        } catch (err) {
          setImageError('Failed to perform search');
          setImageResults([]);
          console.error(err);
        } finally {
          setImageLoading(false);
        }
      })();
    }
    
    // Check for file search params
    const searchType = params.get('searchType');
    const matchType = params.get('matchType');
    const searchTerm = params.get('searchTerm');
    
    if (searchType && matchType && searchTerm) {
      setFileSearchType(searchType as 'filename' | 'filepath');
      setFileMatchType(matchType as 'exact' | 'contains');
      setFileSearchTerm(searchTerm);
      setFileSearchMode('file');
      setActiveTab(1); // Switch to file search tab
      
      // Trigger file search
      (async () => {
        setFileLoading(true);
        setFileError(null);
        setFileSearchSubmitted(true);
        setImageResults([]); // Clear image search results
        setImageError(null);
        
        try {
          const request: FileSearchRequest = {
            searchType: searchType as 'filename' | 'filepath',
            matchType: matchType as 'exact' | 'contains',
            searchTerm: searchTerm,
            limit: 100,
          };
          
          const response = await searchFiles(request);
          if (response.error) {
            setFileError(response.error);
            setFileResults([]);
          } else {
            setFileResults(response.data);
          }
        } catch (err) {
          setFileError('Failed to search files');
          setFileResults([]);
          console.error(err);
        } finally {
          setFileLoading(false);
        }
      })();
    }
  }, [location.search]);

  const handleImageSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageSearchTerm.trim()) return;
    
    // Handle direct navigation for AMI ID and Account ID
    if (imageSearchType === 'ami') {
      navigate(`/image/${imageSearchTerm.trim()}`);
      return;
    }
    if (imageSearchType === 'account') {
      navigate(`/account/${imageSearchTerm.trim()}`);
      return;
    }
    
    // Handle name search
    setImageLoading(true);
    setImageError(null);
    setImageSearchSubmitted(true);
    setFileResults([]); // Clear file results
    setFileError(null);
    
    try {
      const response = await searchAMIs(imageSearchTerm.trim());
      if (response.error) {
        setImageError(response.error);
        setImageResults([]);
      } else {
        setImageResults(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      setImageError('Failed to perform search');
      setImageResults([]);
      console.error(err);
    } finally {
      setImageLoading(false);
    }
  };

  const handleFileSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileSearchTerm.trim()) return;
    
    // Handle hash lookup directly
    if (fileSearchMode === 'hash') {
      navigate(`/hash/${fileSearchTerm.trim()}`);
      return;
    }
    
    // Update URL with search parameters for shareable links
    const params = new URLSearchParams();
    params.set('searchType', fileSearchType);
    params.set('matchType', fileMatchType);
    params.set('searchTerm', fileSearchTerm.trim());
    params.set('limit', '100');
    
    // Update URL without triggering navigation
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Handle file search
    setFileLoading(true);
    setFileError(null);
    setFileSearchSubmitted(true);
    setImageResults([]); // Clear image search results
    setImageError(null);
    
    try {
      const request: FileSearchRequest = {
        searchType: fileSearchType,
        matchType: fileMatchType,
        searchTerm: fileSearchTerm.trim(),
        limit: 100,
      };
      
      const response = await searchFiles(request);
      if (response.error) {
        setFileError(response.error);
        setFileResults([]);
      } else {
        setFileResults(response.data);
      }
    } catch (err) {
      setFileError('Failed to search files');
      setFileResults([]);
      console.error(err);
    } finally {
      setFileLoading(false);
    }
  };

  const handleSort = (field: keyof AMIResponse) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFileSort = (field: keyof FileSearchResult) => {
    if (fileSortField === field) {
      setFileSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setFileSortField(field);
      setFileSortDirection('asc');
    }
  };

  const sortedResults = [...imageResults].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle undefined/null
    if (aValue === undefined || aValue === null) aValue = '';
    if (bValue === undefined || bValue === null) bValue = '';
    // Date fields
    if ([
      'firstSeen', 'lastSeen', 'creationDate', 'deprecationTime'
    ].includes(sortField)) {
      const aTime = new Date(String(aValue)).getTime();
      const bTime = new Date(String(bValue)).getTime();
      if (aTime < bTime) return sortDirection === 'asc' ? -1 : 1;
      if (aTime > bTime) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }
    // String/number fields
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });

  const sortedFileResults = [...fileResults].sort((a, b) => {
    let aValue = a[fileSortField];
    let bValue = b[fileSortField];
    // Handle undefined/null
    if (aValue === undefined || aValue === null) aValue = '';
    if (bValue === undefined || bValue === null) bValue = '';

    // Date fields
    if (fileSortField === 'scanDate' || fileSortField === 'modifiedTimestamp') {
      const aTime = aValue ? new Date(String(aValue)).getTime() : 0;
      const bTime = bValue ? new Date(String(bValue)).getTime() : 0;
      if (aTime < bTime) return fileSortDirection === 'asc' ? -1 : 1;
      if (aTime > bTime) return fileSortDirection === 'asc' ? 1 : -1;
      return 0;
    }

    // String fields
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return fileSortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  const getSearchPlaceholder = () => {
    switch (imageSearchType) {
      case 'ami': return 'e.g., ami-12345678';
      case 'account': return 'e.g., 123456789012';
      case 'name': return 'e.g., ubuntu-20.04, centos*-stream, amzn2-*';
      default: return '';
    }
  };

  const getFileSearchPlaceholder = () => {
    if (fileSearchMode === 'hash') {
      return 'e.g., d41d8cd98f00b204e9800998ecf8427e';
    }
    return fileSearchType === 'filename' ? 'e.g., bash, config.json' : 'e.g., /usr/bin/bash, /etc/config.json';
  };

  return (
    <Container maxWidth="lg" sx={{ px: 2, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mt: 4, mb: 4 }}>
        <Navbar />
      </Box>
      
      <Typography variant="h4" gutterBottom align="center">
        Search
      </Typography>
      <Typography color="text.secondary" paragraph align="center">
        Find AWS AMI images and their files
      </Typography>

      <Paper sx={{ mt: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Search Images" />
          <Tab label="Search Files" />
        </Tabs>

        {/* Image Search Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            <form onSubmit={handleImageSearch}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Search by</FormLabel>
                <RadioGroup
                  row
                  value={imageSearchType}
                  onChange={(e) => setImageSearchType(e.target.value as 'ami' | 'account' | 'name')}
                >                  
                  <FormControlLabel value="name" control={<Radio size="small" />} label="AMI Name" sx={{ mr: 1 }} />
                  <FormControlLabel value="account" control={<Radio size="small" />} label="Account ID" sx={{ mr: 1 }} />
                  <FormControlLabel value="ami" control={<Radio size="small" />} label="AMI ID" sx={{ mr: 0 }} />
                </RadioGroup>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  label="Search term"
                  variant="outlined"
                  size="small"
                  value={imageSearchTerm}
                  onChange={e => setImageSearchTerm(e.target.value)}
                  placeholder={getSearchPlaceholder()}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  size="small"
                  disabled={imageLoading}
                  sx={{ minWidth: 100 }}
                >
                  {imageLoading ? 'Searching...' : 'Search'}
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {imageSearchType === 'ami' && 'Enter an AMI ID to view details'}
                {imageSearchType === 'account' && 'Enter an AWS Account ID to view all AMIs'}
                {imageSearchType === 'name' && 'Search by AMI name with regex support (e.g., ubuntu*-server, amzn2-*-2023)'}
              </Typography>
            </form>
          </Box>
        )}

        {/* File Search Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <form onSubmit={handleFileSearch}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', minWidth: 'auto' }}>Search mode</FormLabel>
                <RadioGroup
                  row
                  value={fileSearchMode}
                  onChange={(e) => setFileSearchMode(e.target.value as 'hash' | 'file')}
                >
                  <FormControlLabel value="file" control={<Radio size="small" />} label="File Name Lookup" sx={{ mr: 1 }} />
                  <FormControlLabel value="hash" control={<Radio size="small" />} label="Hash ID Lookup (MD5)" sx={{ mr: 0 }} />
                </RadioGroup>
              </Box>

              {fileSearchMode === 'file' && (
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel component="legend" sx={{ fontSize: '0.875rem', minWidth: 'auto' }}>Search in:</FormLabel>
                    <RadioGroup
                      row
                      value={fileSearchType}
                      onChange={(e) => setFileSearchType(e.target.value as 'filename' | 'filepath')}
                      sx={{ ml: 0.5 }}
                    >
                      <FormControlLabel 
                        value="filename" 
                        control={<Radio size="small" />} 
                        label="Filename"
                        sx={{ mr: 1 }}
                      />
                      <FormControlLabel 
                        value="filepath" 
                        control={<Radio size="small" />} 
                        label="Full path"
                        sx={{ mr: 0 }}
                      />
                    </RadioGroup>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel component="legend" sx={{ fontSize: '0.875rem', minWidth: 'auto' }}>Match:</FormLabel>
                    <RadioGroup
                      row
                      value={fileMatchType}
                      onChange={(e) => setFileMatchType(e.target.value as 'exact' | 'contains')}
                      sx={{ ml: 0.5 }}
                    >
                      <FormControlLabel 
                        value="exact" 
                        control={<Radio size="small" />} 
                        label="Exact"
                        sx={{ mr: 1 }}
                      />
                      <FormControlLabel 
                        value="contains" 
                        control={<Radio size="small" />} 
                        label="Contains"
                        sx={{ mr: 0 }}
                      />
                    </RadioGroup>
                  </Box>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  label={fileSearchMode === 'hash' ? 'Hash ID' : 'Search term'}
                  variant="outlined"
                  size="small"
                  value={fileSearchTerm}
                  onChange={e => setFileSearchTerm(e.target.value)}
                  placeholder={getFileSearchPlaceholder()}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  size="small"
                  disabled={fileLoading}
                  sx={{ minWidth: 100 }}
                >
                  {fileLoading ? 'Searching...' : fileSearchMode === 'hash' ? 'Lookup' : 'Search'}
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {fileSearchMode === 'hash' && 'Enter a file hash to see which AMIs contain it'}
                {fileSearchMode === 'file' && 'Search for files across all scanned AMIs'}
              </Typography>
            </form>
          </Box>
        )}
      </Paper>

      {/* Loading */}
      {(imageLoading || fileLoading) && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Errors */}
      {imageError && (
        <Alert severity="error" sx={{ mt: 4 }}>
          {imageError}
        </Alert>
      )}

      {fileError && (
        <Alert severity="error" sx={{ mt: 4 }}>
          {fileError}
        </Alert>
      )}

      {/* File Search Results */}
      {fileResults.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            {(() => {
              const uniqueAccounts = new Set(fileResults.map(r => r.accountId)).size;
              const uniqueAmis = new Set(fileResults.map(r => r.amiId)).size;
              return `File Search Results (${fileResults.length}) [${uniqueAccounts} unique accounts, ${uniqueAmis} unique AMIs]`;
            })()}
          </Typography>
          
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={fileSortField === 'accountId'}
                        direction={fileSortField === 'accountId' ? fileSortDirection : 'asc'}
                        onClick={() => handleFileSort('accountId')}
                      >
                        Account ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={fileSortField === 'amiId'}
                        direction={fileSortField === 'amiId' ? fileSortDirection : 'asc'}
                        onClick={() => handleFileSort('amiId')}
                      >
                        AMI ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={fileSortField === 'scanDate'}
                        direction={fileSortField === 'scanDate' ? fileSortDirection : 'asc'}
                        onClick={() => handleFileSort('scanDate')}
                      >
                        Scan Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={fileSortField === 'hash'}
                        direction={fileSortField === 'hash' ? fileSortDirection : 'asc'}
                        onClick={() => handleFileSort('hash')}
                      >
                        Hash ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={fileSortField === 'relativePath'}
                        direction={fileSortField === 'relativePath' ? fileSortDirection : 'asc'}
                        onClick={() => handleFileSort('relativePath')}
                      >
                        File Path
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedFileResults.map((result, index) => (
                    <TableRow
                      key={`${result.amiId}-${result.hash}-${index}`}
                      hover
                    >
                      <TableCell 
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main', 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          minWidth: '100px',
                          '&:hover': { textDecoration: 'underline' } 
                        }}
                        onClick={() => navigate(`/account/${result.accountId}`)}
                      >
                        {result.accountId}
                      </TableCell>
                      <TableCell
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          minWidth: '140px',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/image/${result.amiId}`)}
                      >
                        {result.amiId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          minWidth: '160px',
                        }}
                      >
                        {result.scanDate ? new Date(result.scanDate).toLocaleString() : 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          minWidth: '220px',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/hash/${result.hash}`)}
                      >
                        {result.hash}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main',
                          fontFamily: 'monospace', 
                          fontSize: '0.75rem', 
                          wordBreak: 'break-all',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => {
                          // Set up exact filepath search
                          setFileSearchTerm(result.relativePath);
                          setFileSearchType('filepath');
                          setFileMatchType('exact');
                          setFileSearchMode('file');
                          
                          // Update URL with new search parameters
                          const params = new URLSearchParams();
                          params.set('searchType', 'filepath');
                          params.set('matchType', 'exact');
                          params.set('searchTerm', result.relativePath);
                          params.set('limit', '100');
                          
                          const newUrl = `${location.pathname}?${params.toString()}`;
                          window.history.pushState({}, '', newUrl);
                          
                          // Trigger search
                          handleFileSearch({ preventDefault: () => {} } as React.FormEvent);
                        }}
                      >
                        {result.relativePath}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {sortedFileResults.map((result, index) => (
              <Paper
                key={`${result.amiId}-${result.hash}-${index}`}
                sx={{ p: 2, mb: 2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Account</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/account/${result.accountId}`)}
                    >
                      {result.accountId}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">AMI</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/image/${result.amiId}`)}
                    >
                      {result.amiId}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Scan Date</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                      }}
                    >
                      {result.scanDate ? new Date(result.scanDate).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Hash</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        wordBreak: 'break-all',
                        textAlign: 'right',
                        maxWidth: '70%',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/hash/${result.hash}`)}
                    >
                      {result.hash}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      File Path
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => {
                        // Set up exact filepath search
                        setFileSearchTerm(result.relativePath);
                        setFileSearchType('filepath');
                        setFileMatchType('exact');
                        setFileSearchMode('file');
                        
                        // Update URL with new search parameters
                        const params = new URLSearchParams();
                        params.set('searchType', 'filepath');
                        params.set('matchType', 'exact');
                        params.set('searchTerm', result.relativePath);
                        params.set('limit', '100');
                        
                        const newUrl = `${location.pathname}?${params.toString()}`;
                        window.history.pushState({}, '', newUrl);
                        
                        // Trigger search
                        handleFileSearch({ preventDefault: () => {} } as React.FormEvent);
                      }}
                    >
                      {result.relativePath}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Image Search Results */}
      {imageResults.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            {(() => {
              const uniqueAccounts = new Set(imageResults.map(r => r.ownerId)).size;
              return `Image Search Results (${imageResults.length}) [From ${uniqueAccounts} unique accounts]`;
            })()}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'amiId'}
                      direction={sortField === 'amiId' ? sortDirection : 'asc'}
                      onClick={() => handleSort('amiId')}
                    >
                      AMI ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'ownerId'}
                      direction={sortField === 'ownerId' ? sortDirection : 'asc'}
                      onClick={() => handleSort('ownerId')}
                    >
                      Owner
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'imageOwnerAlias'}
                      direction={sortField === 'imageOwnerAlias' ? sortDirection : 'asc'}
                      onClick={() => handleSort('imageOwnerAlias')}
                    >
                      Owner Alias
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'regionFound'}
                      direction={sortField === 'regionFound' ? sortDirection : 'asc'}
                      onClick={() => handleSort('regionFound')}
                    >
                      Region
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'architecture'}
                      direction={sortField === 'architecture' ? sortDirection : 'asc'}
                      onClick={() => handleSort('architecture')}
                    >
                      Architecture
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'creationDate'}
                      direction={sortField === 'creationDate' ? sortDirection : 'asc'}
                      onClick={() => handleSort('creationDate')}
                    >
                      Creation Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'lastSeen'}
                      direction={sortField === 'lastSeen' ? sortDirection : 'asc'}
                      onClick={() => handleSort('lastSeen')}
                    >
                      Last Seen
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedResults.map((ami) => (
                  <TableRow
                    key={ami.amiId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/image/${ami.amiId}`)}
                  >
                    <TableCell>{ami.amiId}</TableCell>
                    <TableCell>{ami.name}</TableCell>
                    <TableCell>{ami.ownerId}</TableCell>
                    <TableCell>{ami.imageOwnerAlias}</TableCell>
                    <TableCell>{ami.regionFound}</TableCell>
                    <TableCell>{ami.architecture}</TableCell>
                    <TableCell>{ami.creationDate ? new Date(ami.creationDate).toLocaleString() : ''}</TableCell>
                    <TableCell>{ami.lastSeen ? new Date(ami.lastSeen).toLocaleString() : ''}</TableCell>
                    <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ami.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* No Results Messages */}
      {!imageLoading && !imageError && imageResults.length === 0 && imageSearchSubmitted && (
        <Alert severity="info" sx={{ mt: 4 }}>
          No images found matching "{imageSearchTerm}"
        </Alert>
      )}

      {!fileLoading && !fileError && fileResults.length === 0 && fileSearchSubmitted && (
        <Alert severity="info" sx={{ mt: 4 }}>
          No files found matching "{fileSearchTerm}"
        </Alert>
      )}
    </Container>
  );
};

export default Search; 